import React from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const PDFDownloadButton = ({ 
  selectedProperty, 
  selectedPlan, 
  fromDate, 
  toDate, 
  selectedAddone, 
  selectedAddtwo, 
  formData, 
  planSummary, 
  paymentSchedule, 
  installmentDetails,
  checkInDate 
}) => {

  const formatAmount = (amount) => {
    if (!amount) return '0.00';
    const cleanAmount = String(amount).replace(/'/g, '');
    return `${parseFloat(cleanAmount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const tableWidth = pageWidth - margin * 2;
    
    doc.setFontSize(8);
    
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, pageWidth - margin, 8, { align: 'right' });
    doc.text(`This document is private & Confidential`, pageWidth - margin, 13, { align: 'right' });
    
    doc.setFontSize(14);
    doc.setTextColor(40);
    doc.text('Rent Calculation Details', pageWidth / 2, 15, { align: 'center' });
    
    const smallFontSize = 6;
    const normalFontSize = 8;
    

    doc.autoTable({
      startY: 22,
      head: [['Property & Plan Details', '']],
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: normalFontSize
      },
      body: [
        ['Property', selectedProperty?.Name || 'N/A'],
        ['Plan', selectedPlan?.PlanName || 'N/A'],
        ['Check-in Date', checkInDate ? formatDate(checkInDate) : 'N/A'],
        ['Period', `${fromDate ? formatDate(fromDate) : 'N/A'} to ${toDate ? formatDate(toDate) : 'N/A'}`],
        ['Tenure', `${formData.PlanTenur || '0'} months`],
        ['Free Month', formData.FreeMonth || '0'],
        // ['Increment %', `${formData.IncRentPerc || '0'}%`],
        ['Deposit Amount', formatAmount(formData.DepositAmt)]
      ],
      theme: 'grid',
      tableWidth: tableWidth,
      margin: { left: margin, right: margin },
      styles: { fontSize: smallFontSize, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 150 }
      }
    });
    
 
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 2,
      head: [['Addons Information', '']],
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: normalFontSize
      },
      body: [
        ['Addon 1', selectedAddone ? `${selectedAddone.TransPlanName} - ${formatAmount(selectedAddone.Amount)}` : 'N/A'],
        ['Addon 2', selectedAddtwo ? `${selectedAddtwo.MessPlanName} - ${formatAmount(selectedAddtwo.Amount)}` : 'N/A']
      ],
      theme: 'grid',
      tableWidth: tableWidth,
      margin: { left: margin, right: margin },
      styles: { fontSize: smallFontSize, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 40 },
        1: { cellWidth: 150 }
      }
    });

    const paymentColumns = [
      { header: 'Month/Year', dataKey: 'monthYear', width: 22 },
      { header: 'Standard Rent', dataKey: 'standardRent', width: 20 },
      { header: 'Increment', dataKey: 'increment', width: 15 },
      { header: 'Addon 1', dataKey: 'addon1', width: 15 },
      { header: 'Addon 2', dataKey: 'addon2', width: 15 },
      { header: 'Due Date', dataKey: 'date', width: 30 },
      { header: 'Total', dataKey: 'total', width: 20 }
    ];
    
    const paymentData = paymentSchedule.map(item => ({
      monthYear: item.monthYear,
      standardRent: formatAmount(item.standardRent),
      increment: formatAmount(item.extraRentFromIncrement),
      addon1: formatAmount(item.addOneAmount),
      addon2: formatAmount(item.addTwoAmount),
       date: item.dueDate,
      total: formatAmount(item.effectiveRent)
    }));
    
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 2,
      columns: paymentColumns,
      body: paymentData,
      theme: 'grid',
      tableWidth: tableWidth,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: smallFontSize,
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: smallFontSize,
        cellPadding: 1
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    });
 
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 2,
      head: [['Summary', 'Amount']],
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: normalFontSize
      },
      body: [
        ['Total Rent', formatAmount(planSummary.totalRent)],
        ['Increment Amount', formatAmount(planSummary.incrementAmount)],
        ['Addon 1 Total', formatAmount(planSummary.addon1Amount || 0)],
        ['Addon 2 Total', formatAmount(planSummary.addon2Amount || 0)],
        ['Discount', formatAmount(planSummary.discount)],
        ['Net Amount', formatAmount(planSummary.netAmount)]
      ],
      theme: 'grid',
      tableWidth: tableWidth,
      margin: { left: margin, right: margin },
      styles: { fontSize: smallFontSize, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 140 }
      }
    });
    

    const installmentColumns = [
      { header: 'Installment', dataKey: 'number', width: 20 },
      { header: 'Months', dataKey: 'months', width: 20 },
      { header: 'Amount', dataKey: 'amount', width: 30 },
      { header: 'Due Date', dataKey: 'date', width: 30 }
    ];
    
    const installmentData = installmentDetails.map(item => ({
      number: item.installmentNumber,
      months: item.monthsInInstallment,
      amount: formatAmount(item.installmentRent),
      date: item.dueDate
    }));
    
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 2,
      columns: installmentColumns,
      body: installmentData,
      theme: 'grid',
      tableWidth: tableWidth,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: smallFontSize,
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: smallFontSize,
        cellPadding: 1
      }
    });
    
   let startY = doc.lastAutoTable.finalY + 6;

doc.setFontSize(10);
doc.setTextColor(40);
doc.text('Declaration', pageWidth / 2, startY, { align: 'center' });

startY += 5;

doc.setLineWidth(0.5);
doc.line(margin, startY, pageWidth - margin, startY);
startY += 5;

doc.setFontSize(9);
doc.setTextColor(60);

const maxWidth = pageWidth - (margin * 2);

const para1 = `I ____________________ Resident/Father/Mother of _______________________ would like to book Clanbridge Living PVT Ltd girls hostel at ${selectedProperty?.Addr || '______'} for the period of ${formData.PlanTenur || '____'} months.`;
const para2 = `I have understood the payment plan and would be making timely payments in accordance to the payment plan selected by me.`;

const lines1 = doc.splitTextToSize(para1, maxWidth);
lines1.forEach(line => {
  doc.text(line, margin, startY);
  startY += 5;
});

startY += 2;

const lines2 = doc.splitTextToSize(para2, maxWidth);
lines2.forEach(line => {
  doc.text(line, margin, startY);
  startY += 5;
});

startY += 5;


doc.text('Date: _________________', margin, startY);
startY += 7;

const signatureLineLength = 30;
const gapBetweenSignatures = 40; 
const leftSignatureX = margin;
const rightSignatureX = leftSignatureX + signatureLineLength + gapBetweenSignatures;


doc.setLineWidth(0.5);
doc.line(leftSignatureX, startY, leftSignatureX + signatureLineLength, startY);
doc.line(rightSignatureX, startY, rightSignatureX + signatureLineLength, startY);

startY += 5;

doc.setFontSize(8);
doc.text('Parent/Guardian Signature', leftSignatureX, startY, { align: 'left' });
doc.text('CBL Official Signature', rightSignatureX, startY, { align: 'left' });
const rightMarginText = 'Accept as eSigned by applicant in case sent via email';
doc.text(rightMarginText, pageWidth - margin, startY, { align: 'right' });

doc.save(`Rent_Calculation_${selectedProperty?.Name || 'Property'}_${selectedPlan?.PlanName || 'Plan'}.pdf`);
  };

  return (
    <Button
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={handleDownloadPDF}
      sx={{ 
        mr: 1,
        backgroundColor: '#4caf50', 
        color: 'white',
        '&:hover': {
          backgroundColor: '#388e3c'
        }
      }}
    >
      Download PDF
    </Button>
  );
};

export default PDFDownloadButton;